import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import MagicBento, {
  type BentoCardProps,
} from "../react-bits/MagicBento/MagicBento";
import LiquidEther from "../react-bits/LiquidEther/LiquidEther";
import ScrollStack, {
  ScrollStackItem,
} from "../react-bits/ScrollStack/ScrollStack";
import { ROUTES } from "../routes";
import AdSenseSlot from "./AdSenseSlot";
import "./Landing.css";

interface LandingProps {
  onNavigate: (path: string) => void;
  onOpenAuth: () => void;
  user?: { email?: string; username?: string } | null;
}

const toolTabs = [
  ROUTES.carAffordability,
  ROUTES.tax,
  ROUTES.monthlyFinances,
  ROUTES.bondRepayment,
] as const;

const bentoCards: BentoCardProps[] = [
  {
    label: "Affordability",
    title: "Can you afford it?",
    description:
      "Calculate how much home or car you can comfortably afford based on your income, expenses, and interest rates.",
    color: "linear-gradient(135deg, #0f2744 0%, #1e40af 55%, #2563eb 100%)",
    className: "bento-wide bento-affordability",
    cta: "Open calculator →",
  },
  {
    label: "Tax Calculator",
    title: "Know your SARS tax",
    description:
      "Estimate your South African income tax, UIF, and take-home pay for the current tax year.",
    color: "linear-gradient(145deg, #042f2e 0%, #065f46 50%, #059669 100%)",
    className: "bento-tall bento-tax",
    cta: "Open calculator →",
  },
  {
    label: "Bond Repayment",
    title: "Plan your bond",
    description:
      "See monthly repayments, total interest paid, and how rate changes affect your loan over time.",
    color: "linear-gradient(145deg, #451a03 0%, #92400e 50%, #d97706 100%)",
    className: "bento-tall bento-bond",
    cta: "Open calculator →",
  },
  {
    label: "Monthly Finances",
    title: "Track every Rand",
    description:
      "Plan shared or personal monthly budgets, split expenses, and see exactly where your money goes.",
    color: "linear-gradient(135deg, #1e1b4b 0%, #4338ca 55%, #6366f1 100%)",
    className: "bento-wide bento-monthly",
    cta: "Open calculator →",
  },
];

const marketingStacks = [
  {
    tag: "Built for SA",
    title: "Local rules, real numbers",
    body: "Tax brackets, UIF, and bond logic tuned for how South Africans actually budget — not generic overseas templates.",
    gradient: "linear-gradient(145deg, #0c4a6e 0%, #0369a1 50%, #0ea5e9 100%)",
  },
  {
    tag: "No friction",
    title: "Start calculating in seconds",
    body: "No sign-up wall. Open a tool, punch in your figures, and get an answer you can actually use in a conversation.",
    gradient: "linear-gradient(145deg, #1e3a8a 0%, #3730a3 55%, #6366f1 100%)",
  },
  {
    tag: "Save & return",
    title: "Pick up where you left off",
    body: "Sign in when you want to keep scenarios — affordability runs, tax inputs, and monthly budgets stored for next time.",
    gradient: "linear-gradient(145deg, #134e4a 0%, #0f766e 50%, #14b8a6 100%)",
  },
  {
    tag: "Plain language",
    title: "Zero jargon by design",
    body: "Every calculator is built to be understood at the dinner table, not only on a spreadsheet after a finance degree.",
    gradient: "linear-gradient(145deg, #78350f 0%, #b45309 50%, #fbbf24 100%)",
  },
];

const Landing: React.FC<LandingProps> = ({ onNavigate, onOpenAuth }) => {
  const bentoSectionRef = useRef<HTMLElement>(null);
  const [useEther, setUseEther] = useState(true);
  const [useScrollStack, setUseScrollStack] = useState(true);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const isNarrow = window.innerWidth < 768;
    setUseEther(!prefersReduced && !isNarrow);
    setUseScrollStack(!prefersReduced);
  }, []);

  const handleBentoClick = useCallback(
    (index: number) => {
      onNavigate(toolTabs[index]);
    },
    [onNavigate],
  );

  const scrollToBento = useCallback(() => {
    bentoSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const etherColors = useMemo(
    () => ["#0f172a", "#38bdf8", "#7c3aed", "#a78bfa"],
    [],
  );

  return (
    <div className="landing">
      <main id="main-content">
        <section className="landing-hero">
          {useEther ? (
            <div className="hero-ether" aria-hidden="true">
              <LiquidEther
                colors={etherColors}
                mouseForce={18}
                cursorSize={90}
                resolution={0.45}
                autoDemo
                autoSpeed={0.35}
                autoIntensity={1.8}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          ) : (
            <div className="hero-ether-fallback" aria-hidden="true" />
          )}

          <div className="hero-vignette" aria-hidden="true" />

          <div className="hero-content">
            <p className="hero-eyebrow">South African money toolkit</p>
            <h1 className="hero-headline">
              <span className="hero-headline-line">Smarter money </span>
              <span className="hero-headline-accent">
                decisions for South Africans
              </span>
            </h1>
            <p className="hero-subline">
              Purpose-built financial calculators. No sign-up required. Built
              with accuracy and simplicity in mind.
            </p>
            <div className="hero-ctas">
              <button
                type="button"
                className="hero-cta-primary"
                onClick={scrollToBento}
              >
                Explore tools
              </button>
              <button className="hero-cta-secondary" onClick={onOpenAuth}>
                Save your work
              </button>
            </div>
          </div>

          <div className="hero-scroll-hint" aria-hidden="true">
            <span />
          </div>
        </section>

        <section
          className="landing-marketing"
          aria-labelledby="landing-marketing-title"
        >
          <div className="landing-marketing-intro">
            <h2 id="landing-marketing-title">Why Totties?</h2>
            <p>Scroll through to find out.</p>
          </div>

          {useScrollStack ? (
            <ScrollStack
              useWindowScroll
              className="landing-scroll-stack"
              itemDistance={55}
              itemScale={0.04}
              itemStackDistance={20}
              stackPosition="30%"
              scaleEndPosition="18%"
              baseScale={0.88}
              rotationAmount={0}
              blurAmount={0}
            >
              {marketingStacks.map((card) => (
                <ScrollStackItem
                  key={card.title}
                  itemClassName="landing-stack-card"
                >
                  <article
                    className="landing-stack-card-inner"
                    style={{ background: card.gradient }}
                  >
                    <span className="landing-stack-tag">{card.tag}</span>
                    <h3>{card.title}</h3>
                    <p>{card.body}</p>
                  </article>
                </ScrollStackItem>
              ))}
            </ScrollStack>
          ) : (
            <div className="landing-marketing-static">
              {marketingStacks.map((card) => (
                <article
                  key={card.title}
                  className="landing-stack-card-inner landing-stack-card-inner--static"
                  style={{ background: card.gradient }}
                >
                  <span className="landing-stack-tag">{card.tag}</span>
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>
          )}
        </section>

        <section
          ref={bentoSectionRef}
          id="landing-tools"
          className="landing-bento"
          aria-labelledby="landing-bento-title"
        >
          <div className="bento-header">
            <h2 id="landing-bento-title">Everything you need in one place</h2>
            <p>Easy to understand, zero jargon. Pick the one you need.</p>
          </div>

          <MagicBento
            cards={bentoCards}
            gridClassName="card-grid--totties"
            onCardClick={(index) => handleBentoClick(index)}
            glowColor="56, 189, 248"
            enableStars
            enableSpotlight
            enableBorderGlow
            enableMagnetism
            clickEffect
            textAutoHide={false}
          />
        </section>
      </main>

      <AdSenseSlot className="AdSenseSlot-wrapper--landing" />

      <footer className="landing-footer">
        <nav className="landing-footer-nav" aria-label="Footer navigation">
          <Link className="landing-footer-link" to={ROUTES.carAffordability}>
            Car affordability
          </Link>
          <Link className="landing-footer-link" to={ROUTES.homeAffordability}>
            Home affordability
          </Link>
          <Link className="landing-footer-link" to={ROUTES.bondRepayment}>
            Bond repayment
          </Link>
          <Link className="landing-footer-link" to={ROUTES.tax}>
            Tax calculator
          </Link>
          <Link className="landing-footer-link" to={ROUTES.monthlyFinances}>
            Monthly finances
          </Link>
          <Link className="landing-footer-link" to={ROUTES.about}>
            About
          </Link>
        </nav>
        <p>
          Find this useful?{" "}
          <a
            href="https://www.buymeacoffee.com/totties"
            target="_blank"
            rel="noopener noreferrer"
          >
            Buy me a coffee
          </a>
        </p>
        <p className="landing-footer-legal">
          Totties is for informational purposes only and does not constitute
          financial advice.
        </p>
      </footer>
    </div>
  );
};

export default Landing;
